import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import type { ReactNode } from "react";

import type { AdminActivityLogEntry } from "@/lib/api";

export type SortDir = "asc" | "desc";

interface Props {
  entries: AdminActivityLogEntry[];
  searchTerm: string;
  sortBy: string;
  sortDir: SortDir;
  onSort: (col: string) => void;
  loading?: boolean;
}

/** Wrap every case-insensitive match of `term` in <mark> for search highlight. */
function highlight(text: string, term: string): ReactNode {
  if (!term) {
    return text;
  }
  const lower = text.toLowerCase();
  const q = term.toLowerCase();
  const parts: ReactNode[] = [];
  let i = 0;
  let key = 0;
  for (let next = lower.indexOf(q); next !== -1; next = lower.indexOf(q, i)) {
    parts.push(text.slice(i, next));
    parts.push(
      <mark key={key++} className="bg-accent/20 text-primary rounded px-0.5">
        {text.slice(next, next + term.length)}
      </mark>,
    );
    i = next + term.length;
  }
  parts.push(text.slice(i));
  return <>{parts}</>;
}

const COLUMNS: { key: string; label: string; sortable: boolean; right?: boolean }[] = [
  { key: "email", label: "User", sortable: true },
  { key: "action", label: "Action", sortable: true },
  { key: "detail", label: "Detail", sortable: false },
  { key: "created_at", label: "When", sortable: true, right: true },
];

export function ActivityTable({ entries, searchTerm, sortBy, sortDir, onSort, loading }: Props) {
  return (
    <div className="border-border relative overflow-hidden rounded-2xl border bg-white">
      {loading && (
        <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center">
          <span className="text-muted text-[13px]">Loading…</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-border border-b">
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`text-muted px-5 py-3 text-[13px] font-semibold ${col.right ? "text-right" : "text-left"}`}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className={`hover:text-primary inline-flex items-center gap-1 ${col.right ? "flex-row-reverse" : ""}`}
                    >
                      {col.label}
                      {sortBy === col.key ? (
                        sortDir === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ChevronsUpDown className="text-muted h-3 w-3" />
                      )}
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="text-muted px-5 py-10 text-center text-[15px]"
                >
                  No activity found.
                </td>
              </tr>
            ) : (
              entries.map((entry) => {
                const name = `${entry.first_name ?? ""} ${entry.last_name ?? ""}`.trim();
                return (
                  <tr
                    key={entry.id}
                    className="border-border hover:bg-background border-b transition-colors last:border-0"
                  >
                    <td className="px-5 py-4">
                      <div className="text-primary text-[15px] font-medium">
                        {name ? highlight(name, searchTerm) : "—"}
                      </div>
                      <div className="text-muted text-[13px]">
                        {highlight(entry.email ?? entry.user_id, searchTerm)}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="bg-surface-subtle text-muted inline-flex rounded-sm px-2.5 py-1 text-[13px] font-semibold">
                        {highlight(entry.action, searchTerm)}
                      </span>
                    </td>
                    <td className="text-muted px-5 py-4 text-[15px]">
                      {entry.detail ? highlight(entry.detail, searchTerm) : "—"}
                    </td>
                    <td className="text-muted px-5 py-4 text-right text-[15px] whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
